/**
 * dataLayer.ts — conversion-tracking events for the acquisition funnel
 * (Salman's spec, Part C). These push GA4/GTM funnel events to
 * `window.dataLayer`; GTM reads them — the A1 first-party, CSP-clean loader
 * (`gtm.ts`, gated to the acquisition journey, consent-gated via `consent.ts`)
 * installs it. When the GTM container id is unset (fail-closed) a push is a
 * harmless no-op appended to a plain array, so this module is safe on its own.
 *
 * PII/PHI-SAFE BY CONSTRUCTION (Part F / R19 / UK-GDPR). The exported functions'
 * parameter types admit ONLY non-identifying, non-clinical fields — opaque ids,
 * step positions and plan product data. There is deliberately NO way to pass an
 * email, phone, name, DOB, postcode, free text, questionnaire answer, BMI,
 * height, weight, condition, medication, eligibility verdict, reason or stopKey
 * through this API — the compiler rejects unknown keys. Keep it that way: never
 * widen a payload type to carry a direct identifier or clinical data.
 *
 * THE TWO REMOVALS BELONG TOGETHER, and neither is sufficient alone — this is
 * why the consent gate that used to guard the email is gone rather than kept:
 *   - NO CLINICAL VERDICT (Art. 9): `eligibility_result` is a PROGRESSION marker
 *     with no outcome field — see `trackEligibilityResult`.
 *   - NO DIRECT IDENTIFIER: `sign_up` carries the opaque lead id only — see
 *     `trackSignUp`.
 * A verdict is only special-category because it attaches to a person, and an
 * email is only sensitive here because a verdict sits beside it under the same
 * `lead_id`. Consent-gating one while keeping the other still leaves the pair
 * reconstructable by any tag in a container administered outside this estate.
 * Re-adding EITHER re-opens the inference; do not restore one on the grounds
 * that the other is gone.
 *
 * journey_id (Salman B1): the app's server-generated, first-party journey id,
 * minted by the GATEWAY as the `gps_journey_id` cookie and read here — live
 * since gateway#15. `resolveJourneyId()` returns undefined only when the cookie
 * is genuinely absent (organic/gateway-less traffic), and the pushes then omit
 * the key. NEVER generate a journey id client-side (a JS cookie is capped at 7
 * days by Safari — it must be server-set), and never place it in a URL.
 */

/** The gateway-minted journey-id cookie (Part B1). Read-only here. */
const JOURNEY_ID_COOKIE = 'gps_journey_id';

/** A single dataLayer entry — always carries an `event` name. */
type DataLayerEvent = Record<string, unknown> & { event: string };

interface TrackingWindow {
  dataLayer?: unknown[];
}

/** The GTM dataLayer, created lazily as a plain array if GTM has not loaded. */
function dataLayer(): unknown[] {
  if (typeof window === 'undefined') return [];
  const w = window as unknown as TrackingWindow;
  if (!Array.isArray(w.dataLayer)) w.dataLayer = [];
  return w.dataLayer;
}

/**
 * The server-set journey id, or undefined until the backend cookie (B1) exists.
 * Read from the cookie ONLY — never generated here.
 */
export function resolveJourneyId(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${JOURNEY_ID_COOKIE}=`));
  if (!match) return undefined;
  const value = decodeURIComponent(match.slice(JOURNEY_ID_COOKIE.length + 1));
  return value.length > 0 ? value : undefined;
}

/*
 * `adsConsented()` USED TO LIVE HERE and was deliberately deleted, not disabled.
 * It gated the plain-text email on `sign_up`, and that payload no longer carries
 * one — so every remaining push is PII- and PHI-free unconditionally, and a
 * helper that gates nothing is an invitation to put something behind it. GTM
 * still receives the Consent Mode v2 signals; that lane is `consent.ts`, which
 * is where a consent decision belongs. If a future payload genuinely needs a
 * consent gate, that is a signal to keep the value out of `window.dataLayer`
 * altogether and send it server-side.
 */

/**
 * Push one dataLayer entry. Fire-and-forget: GTM replaces `dataLayer.push` with
 * its own synchronous trigger evaluator once the container loads, and that
 * container is configured outside this repo by an external consultant — so it
 * CAN throw back into the caller. A faulty tag must never surface as a failed
 * payment, a swallowed clinical hard stop, or a dead Continue button.
 */
function pushRaw(entry: Record<string, unknown>): void {
  try {
    dataLayer().push(entry);
  } catch {
    // Telemetry only — never rethrow into the clinical / money path.
  }
}

/** Push one event, attaching journey_id when the backend cookie is present. */
function push(event: DataLayerEvent): void {
  const journeyId = resolveJourneyId();
  const payload = journeyId ? { ...event, journey_id: journeyId } : event;
  pushRaw(payload);
}

// ---------------------------------------------------------------------------
// Funnel events (Part C). Every payload type is closed — only the listed keys
// are accepted, so no clinical field can ever be passed.
// ---------------------------------------------------------------------------

/** C1 — a step-0 lead was captured. */
export function trackGenerateLead(input: { leadId: string }): void {
  push({ event: 'generate_lead', lead_id: input.leadId });
}

/** C2 — the questionnaire was started (first question shown). */
export function trackQuestionnaireStart(input: { leadId: string }): void {
  push({ event: 'questionnaire_start', lead_id: input.leadId });
}

/**
 * C3 — one questionnaire screen completed. NO answer content — step position
 * only (the type has no field for an answer).
 */
export function trackQuestionnaireStep(input: {
  leadId: string;
  stepNumber: number;
  totalSteps: number;
}): void {
  push({
    event: 'questionnaire_step',
    lead_id: input.leadId,
    step_number: input.stepNumber,
    total_steps: input.totalSteps,
  });
}

/**
 * C4 — the questionnaire reached a verdict. PROGRESSION ONLY: this says the
 * visitor finished the assessment, and NOTHING about what came back.
 *
 * WHY no verdict field (UK-GDPR Art. 9): `eligible` / `requires_review` used to
 * ride here. A clinical verdict about an identifiable person — the lead_id and
 * journey_id join straight back to a named patient, and `sign_up` puts the
 * email in the same layer — is special-category health data, and
 * `window.dataLayer` is readable by every tag in a container administered
 * OUTSIDE this estate. Consent is not a lawful basis we can rely on for that,
 * so the inference must not be in the page at all. The payload is now
 * IDENTICAL for every verdict, so nothing downstream can distinguish them.
 *
 * The event still fires for everyone: it is the funnel's "assessment
 * completed" step and its count is the denominator for the checkout stages.
 * The eligibility RATE is a clinical metric — it comes from clinic-service
 * analytics (which reads the audit tables), never from ad-tech.
 *
 * The event NAME stays `eligibility_result`: the Part C tag triggers live in
 * the external container, and renaming it here would silently break them.
 */
export function trackEligibilityResult(input: { leadId: string }): void {
  push({ event: 'eligibility_result', lead_id: input.leadId });
}

/** A purchasable plan line — product data only, never patient data. */
interface PlanItem {
  /** Stable SKU — the product-level join key; must not change over time. */
  planSku: string;
  planName: string;
  /** Price as a NUMBER (e.g. 36.00), never a string. */
  planPrice: number;
}

function ecommerceItem(plan: PlanItem, quantity = 1) {
  return {
    item_id: plan.planSku,
    item_name: plan.planName,
    price: plan.planPrice,
    quantity,
    // WHY 'treatment_plan' and not 'weight-loss' (Part F): the category rides on
    // a basket that is joined to an email on `sign_up`, so a clinical category
    // string is a health inference about a named person. Orders-service already
    // sends the generic `item_id: 'treatment_plan'` server-side
    // (`ports/ga4-measurement.port.ts`) — the browser funnel must not be looser
    // than the server one, or the two halves of the same funnel disagree.
    item_category: 'treatment_plan',
  };
}

/** C5 — a plan was selected (add_to_cart). Clears the previous ecommerce object first. */
export function trackAddToCart(input: { leadId: string; plan: PlanItem }): void {
  // Clear any prior ecommerce object so item data is never double-counted (C5).
  pushRaw({ ecommerce: null });
  push({
    event: 'add_to_cart',
    lead_id: input.leadId,
    ecommerce: {
      currency: 'GBP',
      value: input.plan.planPrice,
      items: [ecommerceItem(input.plan)],
    },
  });
}

/**
 * C6 — account created (sign_up). The OPAQUE lead id only.
 *
 * WHY no `user_data` (UK-GDPR): the email and phone used to ride here in plain
 * text for GTM to hash client-side (Enhanced Conversions), gated on an ads
 * grant. Two things make that the wrong trade on THIS product. `window.dataLayer`
 * is readable by every tag in a container administered OUTSIDE this estate, so
 * the gate protects the value from Google's own tags and from nothing else. And
 * the same layer carries `eligibility_result` for the same `lead_id` — a direct
 * identifier sitting beside a weight-management assessment is an Art. 9
 * inference however the two are hashed downstream. The conversion still counts
 * and the funnel denominators are intact: `lead_id` is the join key, and the
 * server-side `purchase` (Part D) is where revenue attribution actually happens.
 *
 * Do NOT reintroduce a direct identifier here — not behind a consent gate, not
 * pre-hashed. If Enhanced Conversions are wanted, they belong on the server-side
 * lane where the value never enters the page.
 */
export function trackSignUp(input: { leadId: string }): void {
  push({ event: 'sign_up', lead_id: input.leadId });
}

/**
 * C7 — card submitted / order authorised (add_payment_info). This is NOT a
 * sale: at this point month 1 is HELD, not charged. The real `purchase`
 * conversion is fired server-side on capture (Part D). `orderId` must be the
 * same id that later appears on the captured payment — the funnel join key.
 *
 * `leadId` is OPTIONAL for one lane only (2026-09-25): a card confirmed on the
 * bank's 3D Secure page comes back to a FRESH document, which no longer holds the
 * journey's opaque lead id (memory only, by design). That paying window still
 * pushes its one C7, with `lead_id` OMITTED — never empty, never guessed — like
 * `checkout_complete` without one. The caller decides when that is allowed
 * (`CheckoutFlow`'s `returnedFromBank`); every other lane without a lead id
 * pushes nothing, as before.
 */
export function trackAddPaymentInfo(input: {
  leadId?: string;
  orderId: string;
  plan: PlanItem;
  /** The amount AUTHORISED at checkout, as a number. */
  authorisedAmount: number;
}): void {
  pushRaw({ ecommerce: null });
  push({
    event: 'add_payment_info',
    ...(input.leadId ? { lead_id: input.leadId } : {}),
    order_id: input.orderId,
    ecommerce: {
      currency: 'GBP',
      value: input.authorisedAmount,
      items: [ecommerceItem(input.plan)],
    },
  });
}

/**
 * C8 — the patient reached the end of checkout (`/thank-you`).
 *
 * ⚠️ THIS IS NOT `purchase`, AND IT MUST NEVER BE RENAMED INTO ONE. Read this
 * before wiring anything downstream to it.
 *
 * AT THIS MOMENT NO MONEY HAS BEEN TAKEN. On the hold route the card is
 * PRE-AUTHORISED at checkout and only CAPTURED when a clinician approves the
 * assessment — hours later, and sometimes never: a rejected assessment releases
 * the hold and the patient pays nothing. `trackAddPaymentInfo` above already
 * says the same thing about card submission ("This is NOT a sale"); this event
 * sits one screen further on and is no more of a sale than that one.
 *
 * Firing `purchase` here would be wrong three separate ways:
 *   1. IT OVER-REPORTS REVENUE by every order a clinician later rejects — money
 *      that was never taken, reported as taken, in the numbers the business
 *      spends against.
 *   2. IT MIS-TIMES ATTRIBUTION by hours. The conversion would land at checkout
 *      instead of at capture, so campaign reporting credits the wrong day and,
 *      for anything with a short attribution window, the wrong campaign.
 *   3. IT DOUBLE-COUNTS. The REAL `purchase` is fired SERVER-SIDE by
 *      orders-service on capture (Part D), deduped on `transaction_id`. That is
 *      the source of truth. A browser-side twin under the same name is a second
 *      conversion for one order.
 * `checkout_complete` is named so that no analyst reading a tag list can
 * reasonably mistake it for a revenue event — which is the point of the name.
 *
 * A RELOAD OF THE CONFIRMATION PAGE WILL FIRE IT AGAIN, and so will anyone who
 * forges a history entry on their own browser. That is tolerable precisely
 * BECAUSE this is a progression marker and not the revenue conversion: the
 * server-side `purchase` is deduped and cannot be moved by anything happening in
 * a page. It is one more reason not to wire this event to money.
 *
 * PAYLOAD: the OPAQUE lead id and nothing else (plus `journey_id`, attached by
 * `push`). No order id, no order reference, no assessment id, no medication, no
 * price — the same rule the URL obeys, for the same reason. `lead_id` is the
 * join key every other funnel event already carries; omitted entirely when there
 * is no lead (a clinician plan-link enters checkout without one), never faked.
 */
export function trackCheckoutComplete(input: { leadId?: string }): void {
  push(input.leadId ? { event: 'checkout_complete', lead_id: input.leadId } : { event: 'checkout_complete' });
}
